import type { ReactNode } from "react";
import React from "react";
import { AppConfigContext } from "../app/appConfig";
import { useAlert } from "../app/notificationToast";
import { SMSTemplateModel } from "../../api/smsTemplates/SMSTemplateModel";
import { SMSTemplateAdapter } from "../../api/smsTemplates/SMSTemplateAdapter";
import type {
  SMSCompanySettings,
  SMSPlaceholderDefinition,
  SMSProviderDefinition,
  SMSTemplate,
} from "../../api/smsTemplates/SMSTemplate.typed";

interface SMSTemplateContextType {
  listSMSPlaceholders: () => Promise<SMSPlaceholderDefinition[] | undefined>;
  listSMSProviders: () => Promise<SMSProviderDefinition[] | undefined>;
  getSMSSettings: () => Promise<SMSCompanySettings | undefined>;
  updateSMSSettings: (
    payload: Partial<SMSCompanySettings>,
  ) => Promise<SMSCompanySettings | undefined>;
  listSMSTemplates: () => Promise<SMSTemplate[] | undefined>;
  createSMSTemplate: (payload: SMSTemplate) => Promise<SMSTemplate | undefined>;
  updateSMSTemplate: (id: number, payload: SMSTemplate) => Promise<SMSTemplate | void | undefined>;
  deleteSMSTemplate: (id: number) => Promise<void>;
}

const SMSTemplateContext = React.createContext<SMSTemplateContextType>(null!);

export function SMSTemplateProvider(props: { children: ReactNode }) {
  const appConfig = React.useContext(AppConfigContext);
  const { showAlert } = useAlert();

  const adapter = React.useMemo(() => {
    if (!appConfig) return null;
    const model = new SMSTemplateModel(appConfig);

    return new SMSTemplateAdapter(model);
  }, [appConfig]);

  const wrap = React.useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
      try {
        return await fn();
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");

        return undefined;
      }
    },
    [showAlert],
  );

  const listSMSPlaceholders = React.useCallback(
    async () => (adapter ? wrap(() => adapter.listPlaceholders()) : undefined),
    [adapter, wrap],
  );

  const listSMSProviders = React.useCallback(
    async () => (adapter ? wrap(() => adapter.listProviders()) : undefined),
    [adapter, wrap],
  );

  const getSMSSettings = React.useCallback(
    async () => (adapter ? wrap(() => adapter.getSettings()) : undefined),
    [adapter, wrap],
  );

  const updateSMSSettings = React.useCallback(
    async (payload: Partial<SMSCompanySettings>) =>
      adapter ? wrap(() => adapter.updateSettings(payload)) : undefined,
    [adapter, wrap],
  );

  const listSMSTemplates = React.useCallback(
    async () => (adapter ? wrap(() => adapter.listTemplates()) : undefined),
    [adapter, wrap],
  );

  const createSMSTemplate = React.useCallback(
    async (payload: SMSTemplate) =>
      adapter ? wrap(() => adapter.createTemplate(payload)) : undefined,
    [adapter, wrap],
  );

  const updateSMSTemplate = React.useCallback(
    async (id: number, payload: SMSTemplate) =>
      adapter ? wrap(() => adapter.updateTemplate(id, payload)) : undefined,
    [adapter, wrap],
  );

  const deleteSMSTemplate = React.useCallback(
    async (id: number) => {
      if (!adapter) return;
      try {
        await adapter.deleteTemplate(id);
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const value = React.useMemo(
    () => ({
      listSMSPlaceholders,
      listSMSProviders,
      getSMSSettings,
      updateSMSSettings,
      listSMSTemplates,
      createSMSTemplate,
      updateSMSTemplate,
      deleteSMSTemplate,
    }),
    [
      listSMSPlaceholders,
      listSMSProviders,
      getSMSSettings,
      updateSMSSettings,
      listSMSTemplates,
      createSMSTemplate,
      updateSMSTemplate,
      deleteSMSTemplate,
    ],
  );

  return <SMSTemplateContext.Provider value={value}>{props.children}</SMSTemplateContext.Provider>;
}

export function useSMSTemplate() {
  const ctx = React.useContext(SMSTemplateContext);
  if (!ctx) throw new Error("useSMSTemplate must be used within SMSTemplateProvider");

  return ctx;
}
